import * as React from 'react'

interface StudioLayoutScrollContextValue {
  scrollerRef: React.RefObject<HTMLDivElement | null>
  getScrollTop: () => number
  resetView: () => void
  setScrollTop: (top: number) => void
}

const StudioLayoutScrollContext = React.createContext<StudioLayoutScrollContextValue | null>(null)

export function StudioLayoutScrollProvider({
  contentRef,
  children,
}: {
  contentRef: React.RefObject<HTMLDivElement | null>
  children: React.ReactNode
}) {
  const getScrollTop = React.useCallback(() => contentRef.current?.scrollTop ?? 0, [contentRef])

  const setScrollTop = React.useCallback(
    (top: number) => {
      const scroller = contentRef.current

      if (scroller) {
        scroller.scrollTop = top
      }
    },
    [contentRef],
  )

  const resetView = React.useCallback(() => {
    setScrollTop(0)
  }, [setScrollTop])

  const value = React.useMemo(
    () => ({ scrollerRef: contentRef, getScrollTop, resetView, setScrollTop }),
    [contentRef, getScrollTop, resetView, setScrollTop],
  )

  return (
    <StudioLayoutScrollContext.Provider value={value}>
      {children}
    </StudioLayoutScrollContext.Provider>
  )
}

export function useStudioLayoutScroll() {
  const context = React.useContext(StudioLayoutScrollContext)

  if (!context) {
    throw new Error('useStudioLayoutScroll must be used within StudioLayout')
  }

  return context
}

/** The content pane StudioLayout scrolls in, or null outside StudioLayout (studiobook, tests). */
export function useStudioLayoutScroller() {
  return React.useContext(StudioLayoutScrollContext)?.scrollerRef ?? null
}

/** Scrolls StudioLayout's content pane back to the top. */
export function useStudioLayoutResetView() {
  return useStudioLayoutScroll().resetView
}
